import { usePayeeDetailDrawer } from '@/features/ap_payees/components/PayeeDetailDrawer/context/PayeeDetailDrawerProvider';
import { useScrollToSectionOnEdit } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/useScrollToSectionOnEdit';
import { SECTION_KEYS } from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { useTranslation } from '@/i18n';
import { ButtonV2 } from '@moneyforward/ap-frontend-components';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { GetApPayeeInfoResponseApInvoiceTransactionDefaultValue } from 'ap-openapi';
import classnames from 'classnames/bind';
import { FC, useCallback, useMemo } from 'react';
import styles from '../../../styles.module.scss';
import { AnchorItemKey } from '../../../types';
import { HeaderSection, SingleRow } from '../../Common';
import { SingleRowProps } from '../../type';
import { Edit } from './Edit';

const cx = classnames.bind(styles);

type TransactionDetailProps = {
  apPayeeId: string;
  transactionDefaultValue?: GetApPayeeInfoResponseApInvoiceTransactionDefaultValue;
  isEditable?: boolean;
};

export const TransactionDetail: FC<TransactionDetailProps> = ({
  apPayeeId,
  transactionDefaultValue,
  isEditable = false,
}) => {
  const { t } = useTranslation();
  const { isEditingSection, setIsEditingSection } = usePayeeDetailDrawer();
  const isEditing = isEditingSection === SECTION_KEYS.TRANSACTION;
  const sectionRef = useScrollToSectionOnEdit(
    isEditing,
    SECTION_KEYS.TRANSACTION
  );

  const useProjectSelect = useBooleanFlagValue(
    'use-project-select-for-report-form',
    false
  );

  const formatTaxIncluded = transactionDefaultValue?.is_tax_included
    ? t('ap_payee_detail_drawer_transaction_detail_yes')
    : t('ap_payee_detail_drawer_transaction_detail_no');

  const rows: SingleRowProps[] = useMemo(() => {
    return [
      {
        label: t('ap_payee_detail_drawer_transaction_detail_item_label', {
          defaultValue: '品目',
        }),
        value: transactionDefaultValue?.name,
      },
      {
        label: t(
          'ap_payee_detail_drawer_transaction_detail_expense_dept_label',
          { defaultValue: '費用負担部門' }
        ),
        value: transactionDefaultValue?.expense_dept?.name,
      },
      {
        label: t(
          'ap_payee_detail_drawer_transaction_detail_expense_item_name_label',
          { defaultValue: '経費科目' }
        ),
        value: transactionDefaultValue?.ap_item?.name,
      },
      {
        label: t('ap_payee_detail_drawer_transaction_detail_project_label', {
          defaultValue: '費用負担プロジェクト',
        }),
        value: useProjectSelect
          ? transactionDefaultValue?.project?.name
          : transactionDefaultValue?.project_code?.name,
      },
      {
        label: t(
          'ap_payee_detail_drawer_transaction_detail_is_tax_included_label',
          { defaultValue: '税込入力' }
        ),
        value: formatTaxIncluded,
      },
      {
        label: t('ap_payee_detail_drawer_transaction_detail_currency_label', {
          defaultValue: '通貨',
        }),
        value: transactionDefaultValue?.default_currency,
      },
    ];
  }, [transactionDefaultValue, formatTaxIncluded, t, useProjectSelect]);

  const onActionButtonClick = useCallback(() => {
    setIsEditingSection(SECTION_KEYS.TRANSACTION);
  }, [setIsEditingSection]);

  if (isEditing) {
    return (
      <div
        id={AnchorItemKey.TRANSACTION_DEFAULT_VALUE}
        ref={sectionRef}
        className={cx('subSectionContainer')}
        key='transaction-detail-sub-section-container'
      >
        <Edit
          apPayeeId={apPayeeId}
          transactionDefaultValue={transactionDefaultValue}
        />
      </div>
    );
  }

  return (
    <div
      id={AnchorItemKey.TRANSACTION_DEFAULT_VALUE}
      ref={sectionRef}
      className={cx('subSectionContainer')}
      key='transaction-detail-sub-section-container'
    >
      <HeaderSection
        displayName={t('ap_payee_detail_drawer_transaction_detail')}
        isSubHeader
        actionButton={
          isEditable && (
            <ButtonV2
              onClick={onActionButtonClick}
              isSecondary
              isDisabled={isEditingSection !== SECTION_KEYS.NULL}
            >
              {t('edit')}
            </ButtonV2>
          )
        }
      />
      <div className={cx('bodySectionContainer')}>
        {rows.map((item, index) => (
          <SingleRow
            key={`${item.label}-${index}`}
            label={item.label}
            value={item.value}
          />
        ))}
      </div>
    </div>
  );
};

TransactionDetail.displayName = 'TransactionDetail';
